import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Typography, Upload, message, Spin, Alert, Progress } from 'antd';
import { UploadOutlined, FileTextOutlined, CloseCircleOutlined } from '@ant-design/icons';
import FileService from '../../services/FileService';
import { cacheManager } from '../../utils/cacheManager';

const { Title, Text } = Typography;
const { Dragger } = Upload;

const Files: React.FC = () => {
  const [uploadingPreprocessed, setUploadingPreprocessed] = useState(false);
  const [hasPreprocessedData, setHasPreprocessedData] = useState<boolean | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [processingMessage, setProcessingMessage] = useState<string>('');
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoadingStatus(true);
      try {
        const status = await FileService.getPreprocessedDataStatus();
        setHasPreprocessedData(status.hasData);
        setFileName(status.fileName || null);
      } catch (error: any) {
        console.error('Failed to fetch preprocessed data status:', error);
        if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
          console.error('Network Error - Check if backend is running on http://localhost:8080');
        }
        setHasPreprocessedData(false);
        setFileName(null);
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchStatus();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const startPolling = useCallback((jobId: string, uploadedFileName: string) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const status = await FileService.getProcessingJobStatus(jobId);
        setProcessingProgress(status.progressPercent || 0);
        setProcessingMessage(status.statusMessage || 'Processing...');

        if (status.status === 'COMPLETED') {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setUploadingPreprocessed(false);
          setHasPreprocessedData(true);
          setFileName(status.fileName || uploadedFileName);
          message.success(
            `Data uploaded! ${status.expensesProcessed || 0} expenses, ${status.locationsProcessed || 0} locations processed.`
          );
          cacheManager.clear();
          window.dispatchEvent(new CustomEvent('filesChanged'));
        } else if (status.status === 'FAILED') {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setUploadingPreprocessed(false);
          setUploadError(status.errorMessage || 'Processing failed');
          message.error('Processing failed: ' + (status.errorMessage || 'Unknown error'));
        }
      } catch (pollError) {
        console.warn('Poll error:', pollError);
      }
    }, 2000);
  }, []);

  const handlePreprocessedUpload = async (file: File) => {
    setUploadError(null);
    setUploadingPreprocessed(true);
    setProcessingProgress(0);
    setProcessingMessage('Uploading file...');
    try {
      const response = await FileService.uploadPreprocessedData(file);
      if (response.jobId) {
        setProcessingMessage('Processing started...');
        startPolling(response.jobId, file.name);
      } else {
        // Fallback for unexpected response format
        setUploadingPreprocessed(false);
        setUploadError('Unexpected response from server');
      }
    } catch (error: any) {
      setUploadingPreprocessed(false);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to upload preprocessed data';
      setUploadError(errorMessage);
      message.error(errorMessage);
    }
    return false;
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
          Files
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Upload preprocessed JSON data to update the system
        </Text>
      </div>

      <Card
        style={{
          maxWidth: 800,
          margin: '0 auto',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UploadOutlined style={{ fontSize: 18, color: '#7a1f2a' }} />
            <span>Upload Preprocessed Data</span>
          </div>
        }
      >
        {loadingStatus ? (
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              background: '#f5f5f5',
              borderRadius: 8,
              border: '1px solid #e8e8e8',
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}
          >
            <Spin size="small" />
            <Text style={{ fontSize: 14, color: '#8c8c8c' }}>
              Checking preprocessed data status...
            </Text>
          </div>
        ) : (
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              background: hasPreprocessedData
                ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                : '#fafafa',
              borderRadius: 8,
              border: '1px solid #e8e8e8',
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}
          >
            <FileTextOutlined style={{ fontSize: 20, color: hasPreprocessedData ? '#7a1f2a' : '#8c8c8c' }} />
            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {hasPreprocessedData ? 'Preprocessed data loaded' : 'No file uploaded'}
              </Text>
              {hasPreprocessedData && fileName && (
                <Text style={{ fontSize: 14, color: '#595959', display: 'block', marginBottom: 4 }}>
                  File: {fileName}
                </Text>
              )}
              <Text style={{ fontSize: 14, color: '#595959' }}>
                {hasPreprocessedData
                  ? 'System is using preprocessed data from the database'
                  : 'Upload a JSON file to populate the system with preprocessed data'}
              </Text>
            </div>
          </div>
        )}

        {uploadError && (
          <Alert
            message="Upload Error"
            description={uploadError}
            type="error"
            icon={<CloseCircleOutlined />}
            closable
            onClose={() => setUploadError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Dragger
          name="preprocessed-file"
          accept=".json,application/json"
          beforeUpload={(file) => {
            setUploadError(null);

            const isValidType = file.type === 'application/json' || file.name.toLowerCase().endsWith('.json');
            if (!isValidType) {
              const errorMsg = `Invalid file type. The file "${file.name}" is not a JSON file. Please upload a file with .json extension.`;
              setUploadError(errorMsg);
              message.error('Invalid file type. Please upload a JSON file (.json).');
              return Upload.LIST_IGNORE;
            }

            const maxSize = 100 * 1024 * 1024; // 100MB
            if (file.size > maxSize) {
              const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
              const errorMsg = `File size (${fileSizeMB} MB) exceeds the 100MB limit. Please upload a smaller file.`;
              setUploadError(errorMsg);
              message.error('File size exceeds 100MB limit. Please upload a smaller file.');
              return Upload.LIST_IGNORE;
            }

            handlePreprocessedUpload(file);
            return false;
          }}
          showUploadList={false}
          disabled={uploadingPreprocessed}
          style={{
            borderRadius: 8,
            border: uploadError ? '2px dashed #ff4d4f' : '2px dashed #d9d9d9',
            background: uploadingPreprocessed ? '#fafafa' : '#fff',
            transition: 'all 0.3s ease'
          }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 16 }}>
            <UploadOutlined style={{ fontSize: 48, color: uploadingPreprocessed ? '#7a1f2a' : '#8c8c8c' }} />
          </p>
          <p className="ant-upload-text" style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
            {uploadingPreprocessed ? 'Processing data...' : 'Click or drag JSON file here to upload'}
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 14, color: '#8c8c8c' }}>
            Supports .json files only. File should be generated by the preprocessing script.
          </p>
          {uploadingPreprocessed && (
            <div style={{ marginTop: 24, padding: '0 24px' }}>
              <Progress
                percent={processingProgress}
                status="active"
                strokeColor="#7a1f2a"
              />
              <Text style={{ display: 'block', marginTop: 8, color: '#595959', fontSize: 13 }}>
                {processingMessage}
              </Text>
            </div>
          )}
        </Dragger>
      </Card>
    </div>
  );
};

export default Files;
